import React, { useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, FormGroup, Label, Row } from "reactstrap";
import { Paperclip } from "react-feather";
import * as Yup from "yup";
import { toast } from "react-toastify";
import moment from "moment";

import { StyledSelect } from "../../../components/StyledSelect";
import { fetchTaskCategories, patchTask } from "../../../services/taskServices";
import useFetcher from "../../../hooks/useFetcher";
import { useFiles } from "../../../hooks/useFiles";
import { handleErrors } from "../../../services/handleErrors";
import { useAuth } from "../../../context/AuthContext";
import { queryDateFormat } from "../../../../config/constants";
import { fetchNovedadPiAsignable } from "../../../services/novedadesServices";
import modulos from "../../../mocks/modulos";

const TaskEdit = ({ taskSelected, newTaskUpdated, isAdmin = false, isAudit /* , isLider = false */ }) => {
  const taskEdit = { ...taskSelected };
  const { novedadId } = taskEdit;
  const { refreshToken, currentUser } = useAuth();
  const [categories, isLoadingCategories] = useFetcher(fetchTaskCategories);
  const [pisAsignable, isLoadingAsigable] = useFetcher(fetchNovedadPiAsignable);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [, handleUploadFileButton, saveFiles] = useFiles();
  const today = moment().format(queryDateFormat);

  const handleConfirmSelectedFiles = (event) => {
    setSelectedFiles(event.target.files);
  };

  const formik = useFormik({
    initialValues: {
      type: taskEdit.type,
      dueDate: moment(taskEdit.dueDate).format(queryDateFormat),
      completeDate: taskEdit.completeDate ? moment(taskEdit.completeDate).format(queryDateFormat) : null,
      observations: taskEdit.observations,
      responsableId: taskEdit.responsableId,
      liderId: taskEdit.liderId,
    },
    onSubmit: async (task) => {
      try {
        const taskToUpdate = { ...task, id: taskEdit.id };
        const { message, result } = await patchTask(novedadId, taskToUpdate);
        if (selectedFiles.length > 0) {
          try {
            await saveFiles({ entity: "tarea", entityId: result.itemId || result.id });
          } catch (error) {
            let errMsg = "";
            handleErrors(
              error,
              (msg) => {
                errMsg = msg;
              },
              refreshToken,
              formik
            );
            toast.error(`Error subiendo los archivos, intente luego desde el detalle de la tarea. ${errMsg}`);
          }
        }

        newTaskUpdated(result);
        toast.success(message);
      } catch (error) {
        handleErrors(error, toast.error, refreshToken, formik);
      }
    },
    validationSchema: Yup.object().shape({
      type: Yup.number().typeError("Debe seleccionar un tipo de la lista").required("Este campo es requerido."),
      responsableId: Yup.number().typeError("Debe seleccionar un responsable").required("Este campo es requerido."),
      liderId: Yup.number().typeError("Debe seleccionar un lider").required("Este campo es requerido."),
      dueDate: Yup.date().typeError("Debe elegir una fecha de vencimiento").required("Este campo es requerio."),
      observations: Yup.string().nullable().notRequired(),
    }),
  });

  const typeOptions = categories.type
    ? categories.type
        .map((tipo) => {
          return { label: tipo.text, value: tipo.id };
        })
        .sort((a, b) => (a.label > b.label ? 1 : -1))
    : [];

  let typeValue = typeOptions.find((option) => {
    return option.value === formik.values.type;
  });

  typeValue = typeValue || null;

  const piOptions = pisAsignable
    ? pisAsignable.map((assigned) => {
        return { label: assigned.nombreApellido, value: assigned.itemId };
      })
    : [];

  let responsableValue = piOptions.find((option) => {
    return option.value === formik.values.responsableId;
  });

  responsableValue = responsableValue || null;

  let liderValue = piOptions.find((option) => {
    return option.value === formik.values.liderId;
  });

  liderValue = liderValue || null;

  return (
    <div className="container-fluid">
      <Form onSubmit={formik.handleSubmit}>
        <Row>
          <Col>
            <FormGroup>
              <Label htmlFor="type" className="col-form-label pt-0 w-100">
                Tipo de tarea
              </Label>
              <StyledSelect
                className={`w-100 ${formik.errors.type && formik.touched.type ? "error-field" : ""}`}
                id="type"
                name="type"
                formik={formik}
                value={typeValue}
                isLoading={isLoadingCategories}
                options={typeOptions}
                placeholder="Seleccione tipo de tarea"
                isClearable
                isDisabled={formik.isSubmitting || isLoadingCategories || !isAdmin}
              />

              {formik.errors.type && formik.touched.type && <div className="input-feedback">{formik.errors.type}</div>}
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col>
            <FormGroup>
              <Label htmlFor="dueDate" className="col-form-label pt-0 w-100">
                Fecha vencimiento
              </Label>

              <input
                className={formik.errors.dueDate && formik.touched.dueDate ? "form-control error-field" : "form-control"}
                type="date"
                name="dueDate"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.dueDate}
                min={today}
                disabled={formik.isSubmitting || !currentUser.getMetadata(modulos.CALENDAR, "canEditDueDate")}
              />

              {formik.errors.dueDate && formik.touched.dueDate && <div className="input-feedback">{formik.errors.dueDate}</div>}
            </FormGroup>
          </Col>
        </Row>
        {taskEdit.completeDate && (
          <Row>
            <Col>
              <FormGroup>
                <Label htmlFor="dueDate" className="col-form-label pt-0 w-100">
                  Fecha de completado
                </Label>
                <input
                  className={formik.errors.completeDate && formik.touched.completeDate ? "form-control error-field" : "form-control"}
                  type="date"
                  name="completeDate"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.completeDate}
                  min={today}
                  disabled={!isAdmin}
                />
                {formik.errors.completeDate && formik.touched.completeDate && <div className="input-feedback">{formik.errors.completeDate}</div>}
              </FormGroup>
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <FormGroup>
              <Label className="col-form-label pt-0"> Responsable </Label>
              <StyledSelect
                className={`w-100 ${formik.errors.responsableId && formik.touched.responsableId ? "error-field" : ""}`}
                id="responsableId"
                name="responsableId"
                formik={formik}
                value={responsableValue}
                placeholder="Seleccione asignado"
                isClearable
                isLoading={isLoadingAsigable}
                options={piOptions}
                isDisabled={formik.isSubmitting || isLoadingAsigable || !isAdmin}
              />
              {formik.errors.responsableId && formik.touched.responsableId && <div className="input-feedback">{formik.errors.responsableId}</div>}
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col>
            <FormGroup>
              <Label className="col-form-label pt-0"> Lider </Label>
              <StyledSelect
                className={`w-100 ${formik.errors.liderId && formik.touched.liderId ? "error-field" : ""}`}
                id="liderId"
                name="liderId"
                formik={formik}
                value={liderValue}
                placeholder="Seleccione asignado"
                isClearable
                isLoading={isLoadingAsigable}
                options={piOptions}
                isDisabled={formik.isSubmitting || isLoadingAsigable || !isAdmin}
              />
              {formik.errors.liderId && formik.touched.liderId && <div className="input-feedback">{formik.errors.liderId}</div>}
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col>
            <FormGroup>
              <Label htmlFor="observations" className="col-form-label pt-0 w-100">
                Observaciones
              </Label>
              <textarea
                className={formik.errors.observations && formik.touched.observations ? "form-control error-field" : "form-control"}
                name="observations"
                disabled={formik.isSubmitting}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.observations}
              />
              {formik.errors.observations && formik.touched.observations && <div className="input-feedback">{formik.errors.observations}</div>}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col>
            <FormGroup>
              <Form className="p-absolute" style={{ height: "0px", width: "0px", overflow: "hidden" }}>
                <input
                  id="upfile"
                  type="file"
                  onChange={handleConfirmSelectedFiles}
                  multiple
                  accept="image/jpeg,image/gif,
                    image/png,application/pdf,
                    application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                    application/vnd.ms-excel,
                    application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                />
              </Form>
              <Label className="col-form-label pt-0 w-100">
                <p>Adjuntar archivos </p>
                <b>
                  <p>* Le recordamos que no puede subir información referida a los asociados.</p>
                </b>
              </Label>
              <Button color="link" size="xs" onClick={handleUploadFileButton} disabled={isAudit}>
                <Paperclip size="24" />
              </Button>
              {selectedFiles.length > 0 ? (
                <div className="normalized_ul">
                  <Label className="col-form-label pt-0 w-100"> Archivos adjuntos </Label>
                  <ul>
                    {Array.from(selectedFiles).map((file) => (
                      <li>{file.name}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <></>
              )}
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Button type="submit" disabled={formik.isSubmitting || isAudit}>
            Guardar Cambios
          </Button>
        </FormGroup>
      </Form>
    </div>
  );
};

export default TaskEdit;
